import { Alert, Button, Snackbar } from "@mui/material";
import { useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

const PWA_UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

let pwaUpdateMonitorStarted = false;

async function cleanupDevelopmentServiceWorkers() {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));

  if ("caches" in window) {
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames
        .filter(
          (cacheName) => cacheName.startsWith("workbox-") || cacheName.includes("geoanalytics")
        )
        .map((cacheName) => caches.delete(cacheName))
    );
  }
}

async function requestServiceWorkerUpdate(
  swScriptUrl: string,
  registration: ServiceWorkerRegistration
) {
  if (registration.installing || !navigator.onLine || document.visibilityState === "hidden") {
    return;
  }

  try {
    const response = await fetch(swScriptUrl, {
      cache: "no-store",
      headers: {
        cache: "no-store",
        "cache-control": "no-cache"
      }
    });

    if (response.ok) {
      await registration.update();
    }
  } catch {
    // Network failures are transient; the next interval/focus/online event will retry.
  }
}

function startPwaUpdateMonitor(
  swScriptUrl: string,
  registration: ServiceWorkerRegistration | undefined
) {
  if (!registration || pwaUpdateMonitorStarted) {
    return;
  }

  pwaUpdateMonitorStarted = true;

  const checkForUpdate = () => {
    void requestServiceWorkerUpdate(swScriptUrl, registration);
  };
  const checkForVisibleUpdate = () => {
    if (document.visibilityState === "visible") {
      checkForUpdate();
    }
  };

  window.setInterval(checkForUpdate, PWA_UPDATE_CHECK_INTERVAL_MS);
  window.addEventListener("focus", checkForUpdate);
  window.addEventListener("online", checkForUpdate);
  document.addEventListener("visibilitychange", checkForVisibleUpdate);
  checkForUpdate();
}

function DevelopmentUpdateCleanup() {
  useEffect(() => {
    void cleanupDevelopmentServiceWorkers();
  }, []);

  return null;
}

function ProductionUpdatePrompt() {
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(false);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW: startPwaUpdateMonitor
  });

  const handleUpdate = async () => {
    setUpdating(true);
    setUpdateError(false);

    let reloaded = false;
    const reload = () => {
      if (!reloaded) {
        reloaded = true;
        window.location.reload();
      }
    };

    navigator.serviceWorker?.addEventListener("controllerchange", reload, { once: true });

    try {
      await updateServiceWorker(true);
      window.setTimeout(reload, 1200);
    } catch {
      setUpdating(false);
      setUpdateError(true);
    }
  };

  return (
    <Snackbar open={needRefresh} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
      <Alert
        severity={updateError ? "warning" : "info"}
        action={
          <>
            <Button
              color="inherit"
              size="small"
              disabled={updating}
              onClick={() => void handleUpdate()}
            >
              {updating ? "Atualizando" : "Atualizar agora"}
            </Button>
            <Button
              color="inherit"
              size="small"
              disabled={updating}
              onClick={() => setNeedRefresh(false)}
            >
              Depois
            </Button>
          </>
        }
      >
        {updateError
          ? "Não foi possível aplicar a atualização automaticamente."
          : "Uma nova versão do GeoAnalytics está disponível."}
      </Alert>
    </Snackbar>
  );
}

export function UpdatePrompt() {
  if (import.meta.env.DEV) {
    return <DevelopmentUpdateCleanup />;
  }

  return <ProductionUpdatePrompt />;
}
